'use client';

import React, { useEffect, useState } from 'react';
// Icons
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
// Material UI
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import client from '../../utils/axiosInstance';
const MAX_LEVELS = 5;
const MAX_OPTIONS = 100; // Cambiado a 100
const PREVIEW_LIMIT = 10; // Mostrar solo 10 en el card

const uuid = () => crypto.randomUUID();

export default function DemographicDataForm({
  demographics,
  setDemographics,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [internalId] = useState(() => crypto.randomUUID()); // ← ID ÚNICO PARA ESTE COMPONENTE
  const [selectedExcel, setSelectedExcel] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  //const [demographics, setDemographics] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [demographicToDelete, setDemographicToDelete] = useState(null);
  const [form, setForm] = useState({
    demographicIndex: -1,
    levelNames: [''],
    pathValues: [],
    pathIds: [],
    options: [], // ← Vacío por defecto
    allowMultipleSelection: true,
  });

  // Función para eliminar demográfico
  const handleDeleteDemographic = (index) => {
    setDemographicToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (demographicToDelete !== null) {
      setDemographics((prev) =>
        prev.filter((_, index) => index !== demographicToDelete)
      );
      enqueueSnackbar('Demográfico eliminado correctamente', {
        variant: 'success',
      });
    }
    setDeleteDialogOpen(false);
    setDemographicToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDemographicToDelete(null);
  };

  // ----------------------
  // RESETEAR AL CERRAR MODAL
  // ----------------------

  const resetForm = () => {
    setForm({
      demographicIndex: -1,
      levelNames: [''],
      pathValues: [],
      pathIds: [],
      options: [], // ← Vacío
      allowMultipleSelection: true,
    });
    setStep(1);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  // ----------------------
  // CARGAR DATOS AL SELECCIONAR DEMOGRÁFICO EXISTENTE
  // ----------------------

  useEffect(() => {
    if (form.demographicIndex >= 0 && demographics[form.demographicIndex]) {
      const selectedDemo = demographics[form.demographicIndex];
      setForm((prev) => ({
        ...prev,
        levelNames: [...selectedDemo.levelNames],
        allowMultipleSelection:
          selectedDemo.allowMultipleSelection ?? true,
      }));
    }
  }, [form.demographicIndex, demographics]);

  // ----------------------
  // CARGAR OPCIONES CUANDO SE SELECCIONA UN PATH (o raíz)
  // ----------------------

  useEffect(() => {
    if (step === 3 && form.demographicIndex >= 0) {
      const demo = demographics[form.demographicIndex];
      if (!demo) return;

      const currentLevel = form.pathIds.length;
      const existingOptions = getOptionsAtLevel(
        form.demographicIndex,
        currentLevel,
        form.pathValues
      );

      // Cargar opciones existentes o dejar vacío si no hay
      setForm((prev) => ({
        ...prev,
        options: existingOptions.length > 0 ? [...existingOptions] : [],
      }));
    }
  }, [step, form.pathValues, form.demographicIndex]);

  // ----------------------
  // NAVEGACIÓN DEL ÁRBOL
  // ----------------------

  const findNode = (nodes, path, level = 0) => {
    if (path.length === 0 || level >= path.length) return null;
    const node = nodes.find((n) => n.value === path[level]);
    if (!node) return null;
    if (level === path.length - 1) return node;
    return findNode(node.children, path, level + 1);
  };

  const getOptionsAtLevel = (demoIndex, level, path) => {
    if (demoIndex < 0) return [];
    const demo = demographics[demoIndex];
    if (!demo) return [];
    if (level === 0) return demo.nodes.map((n) => n.value);
    const parent = findNode(demo.nodes, path.slice(0, level));
    return parent?.children?.map((n) => n.value) ?? [];
  };

  const getNodeIdByValue = (nodes, value, path, level = 0) => {
    if (level >= path.length) {
      const node = nodes.find((n) => n.value === value);
      return node?.id || null;
    }
    const currentNode = nodes.find((n) => n.value === path[level]);
    if (!currentNode) return null;
    if (level === path.length - 1) {
      const childNode = currentNode.children.find((n) => n.value === value);
      return childNode?.id || null;
    }
    return getNodeIdByValue(currentNode.children, value, path, level + 1);
  };

  // ----------------------
  // FORMULARIO
  // ----------------------

  const addLevelName = () => {
    if (form.levelNames.length < MAX_LEVELS) {
      setForm({ ...form, levelNames: [...form.levelNames, ''] });
    }
  };

  const updateLevelName = (index, value) => {
    const updated = [...form.levelNames];
    updated[index] = value;
    setForm({ ...form, levelNames: updated });
  };

  const updateAllowMultipleSelection = (value) => {
    setForm({ ...form, allowMultipleSelection: value });
  };

  const removeLevelName = (index) => {
    if (form.levelNames.length > 1) {
      setForm({
        ...form,
        levelNames: form.levelNames.filter((_, i) => i !== index),
      });
    }
  };

  const handlePathSelection = (level, value) => {
    if (form.demographicIndex < 0) return;
    const demo = demographics[form.demographicIndex];
    if (!demo) return;

    const newPathValues = [...form.pathValues.slice(0, level), value];
    const newPathIds = [];

    for (let i = 0; i <= level; i++) {
      const nodeId = getNodeIdByValue(
        demo.nodes,
        newPathValues[i],
        newPathValues.slice(0, i)
      );
      if (nodeId) newPathIds.push(nodeId);
    }

    setForm({ ...form, pathValues: newPathValues, pathIds: newPathIds });
  };

  const addOption = () => {
    if (form.options.length < MAX_OPTIONS) {
      setForm({ ...form, options: [...form.options, ''] });
    }
  };

  const removeOption = (index) => {
    setForm({ ...form, options: form.options.filter((_, i) => i !== index) });
  };

  const updateOption = (index, value) => {
    const updated = [...form.options];
    updated[index] = value;
    setForm({ ...form, options: updated });
  };

  // ----------------------
  // VALIDACIÓN
  // ----------------------

  const step2Valid = form.levelNames.every((x) => x.trim() !== '');
  const step3Valid =
    form.options.every((x) => x.trim() !== '') && form.options.length > 0;

  // ----------------------
  // CONSTRUIR NODOS
  // ----------------------

  const buildNodes = (options, pathIds) => {
    return options.map((value) => ({
      id: uuid(),
      parentId: pathIds.length === 0 ? null : pathIds[pathIds.length - 1],
      value: value.trim(),
      children: [],
    }));
  };

  // ----------------------
  // GUARDAR
  // ----------------------

  const saveDemographic = () => {
    if (!step3Valid) return;

    const newNodes = buildNodes(form.options, form.pathIds);

    setDemographics((prev) => {
      // NUEVO DEMOGRÁFICO
      if (form.demographicIndex === -1) {
        const newDemo = {
          levelNames: form.levelNames,
          nodes: newNodes,
          allowMultipleSelection: form.allowMultipleSelection,
        };

        // 🔥 VERIFICAR SI YA EXISTE UN DEMOGRÁFICO CON EL MISMO NOMBRE
        const existingIndex = prev.findIndex(
          (d) => d.levelNames[0] === newDemo.levelNames[0]
        );

        if (existingIndex === -1) {
          // No existe, agregar como nuevo
          return [...prev, newDemo];
        } else {
          // Ya existe, combinar
          return prev.map((d, idx) =>
            idx === existingIndex ? mergeNodes(d, newDemo) : d
          );
        }
      }

      // ACTUALIZAR EXISTENTE
      return prev.map((d, index) => {
        if (index !== form.demographicIndex) return d;

        const updatedLevelNames = [...form.levelNames];

        // Sin path = agregar en raíz (combinando si hay duplicados)
        if (form.pathIds.length === 0) {
          const replacedNodes = newNodes.map((newNode, index) => {
            // Buscar por value actual primero (si no cambió el nombre)
            const existingByValue = d.nodes.find((n) => n.value === newNode.value);
            if (existingByValue) {
              return { ...newNode, id: existingByValue.id, children: existingByValue.children };
            }

            // Si no encontró por value, buscar por posición (nombre cambió)
            const existingByIndex = d.nodes[index];
            if (existingByIndex) {
              return { ...newNode, id: existingByIndex.id, children: existingByIndex.children };
            }

            return newNode;
          });

          return {
            ...d,
            levelNames: updatedLevelNames,
            nodes: replacedNodes,
            allowMultipleSelection: form.allowMultipleSelection,
          };
        }

        // Con path = agregar como hijo
        const insert = (nodes, pathValues, level = 0) =>
          nodes.map((node) => {
            if (node.value === pathValues[level]) {
              if (level === pathValues.length - 1) {
                // Mapa de hijos existentes para preservar sus propios sub-hijos
                const existingChildrenMap = new Map(
                  (node.children || []).map((c) => [c.value, c])
                );

                // Reemplazar lista con los nuevos valores,
                // preservando sub-hijos de los nodos que ya existían
                const replacedChildren = newNodes.map((newChild) => {
                  const existing = existingChildrenMap.get(newChild.value);
                  return existing
                    ? { ...newChild, id: existing.id, children: existing.children }
                    : newChild;
                });

                return { ...node, children: replacedChildren };
              }
              return {
                ...node,
                children: insert(node.children, pathValues, level + 1),
              };
            }
            return node;
          });

        return {
          ...d,
          levelNames: updatedLevelNames,
          nodes: insert(d.nodes, form.pathValues),
          allowMultipleSelection: form.allowMultipleSelection,
        };
      });
    });

    setDialogOpen(false);
    resetForm();
  };
  const removeSelectedExcel = () => {
    setSelectedExcel(null);
    document.getElementById(`excel-upload-demographics-${internalId}`).value = ''; // ← ID ÚNICO
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await client.post('excel-to-demographics', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      enqueueSnackbar('Excel cargado correctamente', { variant: 'success' });

      // 🔥 COMBINAR demográficos existentes con los nuevos
      const newDemographics = response.data.demographics.map((demo) => ({
        ...demo,
        allowMultipleSelection: demo.allowMultipleSelection ?? true,
      }));

      setDemographics((prevDemographics) => {
        return mergeDemographics(prevDemographics, newDemographics);
      });

      setSelectedExcel(null);
    } catch (error) {
      console.error('❌ Error subiendo archivo:', error);
      enqueueSnackbar('Error subiendo archivo al servidor', {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);

      const fileInput = document.getElementById(`excel-upload-demographics-${internalId}`); // ← ID ÚNICO
      if (fileInput) fileInput.value = '';
    }
  };

  // ----------------------
  // FUNCIÓN PARA COMBINAR DEMOGRÁFICOS
  // ----------------------

  const mergeDemographics = (existing, incoming) => {
    const merged = [...existing];

    incoming.forEach((newDemo) => {
      // Buscar si ya existe un demográfico con el mismo nombre de nivel principal
      const existingIndex = merged.findIndex(
        (demo) => demo.levelNames[0] === newDemo.levelNames[0]
      );

      if (existingIndex === -1) {
        // No existe, agregar como nuevo
        merged.push(newDemo);
      } else {
        // Ya existe, combinar nodos
        merged[existingIndex] = mergeNodes(merged[existingIndex], newDemo);
      }
    });

    return merged;
  };

  const handleEditDemographic = (index) => {
    const demo = demographics[index];
    setForm({
      demographicIndex: index,
      levelNames: [...demo.levelNames],
      pathValues: [],
      pathIds: [],
      options: demo.nodes.map((n) => n.value), // carga opciones raíz
      originalRootValues: demo.nodes.map((n) => n.value),
      allowMultipleSelection: demo.allowMultipleSelection ?? true,
    });
    setStep(3);
    setDialogOpen(true);
  };

  const mergeNodes = (existingDemo, newDemo) => {
    // Combinar levelNames (mantener el más largo)
    const combinedLevelNames =
      existingDemo.levelNames.length > newDemo.levelNames.length
        ? existingDemo.levelNames
        : newDemo.levelNames;

    // Combinar nodos recursivamente
    const combinedNodes = [...existingDemo.nodes];

    newDemo.nodes.forEach((newNode) => {
      const existingNodeIndex = combinedNodes.findIndex(
        (node) => node.value === newNode.value
      );

      if (existingNodeIndex === -1) {
        // Nodo no existe, agregarlo
        combinedNodes.push(newNode);
      } else {
        // Nodo existe, combinar sus hijos
        combinedNodes[existingNodeIndex] = mergeNodeChildren(
          combinedNodes[existingNodeIndex],
          newNode
        );
      }
    });

    return {
      levelNames: combinedLevelNames,
      nodes: combinedNodes,
      allowMultipleSelection:
        newDemo.allowMultipleSelection ??
        existingDemo.allowMultipleSelection ??
        true,
    };
  };

  const mergeNodeChildren = (existingNode, newNode) => {
    if (!newNode.children || newNode.children.length === 0) {
      return existingNode;
    }

    const combinedChildren = [...(existingNode.children || [])];

    newNode.children.forEach((newChild) => {
      const existingChildIndex = combinedChildren.findIndex(
        (child) => child.value === newChild.value
      );

      if (existingChildIndex === -1) {
        // Hijo no existe, agregarlo
        combinedChildren.push(newChild);
      } else {
        // Hijo existe, combinar recursivamente
        combinedChildren[existingChildIndex] = mergeNodeChildren(
          combinedChildren[existingChildIndex],
          newChild
        );
      }
    });

    return {
      ...existingNode,
      children: combinedChildren,
    };
  };

  // ----------------------
  // HELPERS UI
  // ----------------------

  const getCurrentLevelName = () => {
    if (form.demographicIndex === -1) {
      return form.levelNames[0] || 'Nivel 1';
    }
    const demo = demographics[form.demographicIndex];
    const currentLevel = form.pathIds.length;
    return demo?.levelNames[currentLevel] || `Nivel ${currentLevel + 1}`;
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const renderTree = (nodes, levelNames, currentLevel = 0) =>
    nodes.map((n) => {
      const hasChildren = n.children.length > 0;
      const isExpanded = expandedNodes[n.id];
      const previewChildren = n.children.slice(0, PREVIEW_LIMIT);
      const hasMore = n.children.length > PREVIEW_LIMIT;

      return (
        <Box key={n.id} ml={3} my={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              label={levelNames[currentLevel] || `Nivel ${currentLevel + 1}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography fontWeight={600}>{n.value}</Typography>
            {hasChildren && (
              <>
                <Chip label={`${n.children.length} hijos`} size="small" />
                <IconButton size="small" onClick={() => toggleNode(n.id)}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </>
            )}
            {!hasChildren && (
              <span style={{ marginRight: '4px', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1' }}>−</span>
            )}
          </Stack>

          {hasChildren && (
            <Collapse in={isExpanded}>
              <Box borderLeft="2px solid #ddd" pl={2} mt={1}>
                {renderTree(
                  isExpanded ? n.children : previewChildren,
                  levelNames,
                  currentLevel + 1
                )}
                {hasMore && !isExpanded && (
                  <Typography variant="caption" color="text.secondary" ml={3}>
                    ... y {n.children.length - PREVIEW_LIMIT} más
                  </Typography>
                )}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    });

  // ----------------------
  // UI PRINCIPAL
  // ----------------------

  return (
    <Box sx={{ mb: '30px' }}>
      {/* HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Demográficos Jerárquicos
          </Typography>
          <Typography color="text.secondary">
            Gestiona demográficos con múltiples niveles
          </Typography>
        </Box>
        {/* Input oculto */}
        <input
          type="file"
          id={`excel-upload-demographics-${internalId}`} // ← ID ÚNICO
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          disabled={isUploading}
          onChange={handleExcelUpload}
        />

        <Stack direction="row" spacing={2}>
          {/* Botón original */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Agregar Datos
          </Button>

          {/* Nuevo botón */}
          {!selectedExcel && (
            <Button
              variant="outlined"
              color="secondary"
              disabled={isUploading}
              onClick={() => document.getElementById(`excel-upload-demographics-${internalId}`).click()} // ← ID ÚNICO
            >
              {isUploading ? 'Subiendo...' : 'Subir Excel'}
            </Button>
          )}

          {/* Si SÍ hay archivo seleccionado */}
          {selectedExcel && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip label={selectedExcel.name} color="success" />
              <Button
                color="error"
                variant="outlined"
                onClick={removeSelectedExcel}
              >
                Eliminar
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* LISTA */}
      {!Array.isArray(demographics) || demographics.length === 0 ? (
        <Card sx={{ p: 4 }}>
          <Typography align="center" color="text.secondary">
            No hay demográficos creados.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {demographics.map((d, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card>
                <CardHeader
                  title={d.levelNames[0]}
                  subheader={`${d.nodes.length} nodo(s) raíz`}
                  action={
                    <Stack direction="row">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditDemographic(index)}
                        aria-label="editar demográfico"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDemographic(index)}
                        aria-label="eliminar demográfico"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  }
                />
                <Box sx={{ px: 2, pb: 1 }}>
                  <Chip
                    size="small"
                    color={d.allowMultipleSelection ?? true ? 'success' : 'default'}
                    variant="outlined"
                    label={
                      d.allowMultipleSelection ?? true
                        ? 'Permite varias respuestas'
                        : 'Solo una respuesta'
                    }
                  />
                </Box>
                <CardContent>{renderTree(d.nodes, d.levelNames)}</CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* DIALOG */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {step === 1 && 'Paso 1: Seleccionar Demográfico'}
          {step === 2 && 'Paso 2: Definir Niveles'}
          {step === 3 && 'Paso 3: Agregar Valores'}
        </DialogTitle>

        <DialogContent dividers>
          {/* PASO 1: SELECCIONAR */}
          {step === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                ¿Quieres crear un nuevo demográfico o agregar datos a uno
                existente?
              </Alert>

              <Button
                fullWidth
                variant="outlined"
                sx={{ mb: 2 }}
                onClick={() => {
                  setForm({
                    ...form,
                    demographicIndex: -1,
                    levelNames: [''],
                    options: [],
                    allowMultipleSelection: true,
                  });
                  setStep(2);
                }}
              >
                ✨ Crear Nuevo Demográfico
              </Button>

              {Array.isArray(demographics) && demographics.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                    O selecciona uno existente:
                  </Typography>
                  {demographics.map((d, idx) => (
                    <Button
                      key={idx}
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 1 }}
                      onClick={() => {
                        setForm({
                          ...form,
                          demographicIndex: idx,
                          options: [],
                          allowMultipleSelection:
                            d.allowMultipleSelection ?? true,
                        });
                        setStep(3);
                      }}
                    >
                      📁 {d.levelNames[0]}
                    </Button>
                  ))}
                </>
              )}
            </>
          )}

          {/* PASO 2: DEFINIR NIVELES */}
          {step === 2 && form.demographicIndex === -1 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Define los niveles del demográfico
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                Define el nombre para cada nivel jerárquico
              </Typography>

              <Paper sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.allowMultipleSelection}
                      onChange={(e) =>
                        updateAllowMultipleSelection(e.target.checked)
                      }
                    />
                  }
                  label="Permitir selección múltiple al responder"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Esta configuración aplica al demográfico padre. Los niveles hijos
                  se mantienen como parte de la jerarquía.
                </Typography>
              </Paper>

              {form.levelNames.map((levelName, index) => (
                <Stack direction="row" spacing={2} mt={2} key={index}>
                  <TextField
                    fullWidth
                    label={`Nivel ${index + 1}`}
                    value={levelName}
                    onChange={(e) => updateLevelName(index, e.target.value)}
                  />
                  {form.levelNames.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeLevelName(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              ))}

              {form.levelNames.length < MAX_LEVELS && (
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  startIcon={<AddIcon />}
                  onClick={addLevelName}
                >
                  Agregar Nivel
                </Button>
              )}
            </>
          )}

          {/* PASO 3: AGREGAR VALORES */}
          {step === 3 && (
            <>
              {form.demographicIndex >= 0 && form.pathIds.length === 0 && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.allowMultipleSelection}
                        onChange={(e) =>
                          updateAllowMultipleSelection(e.target.checked)
                        }
                      />
                    }
                    label="Permitir selección múltiple al responder"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Esta configuración aplica al demográfico padre.
                  </Typography>
                </Paper>
              )}

              {/* Mostrar levelNames SOLO COMO REFERENCIA */}
              {form.demographicIndex >= 0 &&
                form.levelNames.length > 1 &&
                demographics[form.demographicIndex]?.nodes.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Estructura de Niveles
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {form.levelNames.map((levelName, index) => (
                          <Chip
                            key={index}
                            label={`${index + 1}. ${levelName}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </>
                )}

              {/* Seleccionar ubicación */}
              {form.demographicIndex >= 0 &&
                form.levelNames.length > 1 &&
                demographics[form.demographicIndex]?.nodes.length > 0 && (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Puedes agregar valores en la raíz o como hijos de un nodo
                      existente
                    </Alert>

                    <Typography variant="subtitle2" gutterBottom>
                      ¿Dónde quieres agregar los valores?
                    </Typography>

                    {[...Array(form.levelNames.length - 1)].map((_, level) => {
                      const options = getOptionsAtLevel(
                        form.demographicIndex,
                        level,
                        form.pathValues
                      );
                      if (options.length === 0) return null;
                      if (level > 0 && !form.pathValues[level - 1]) return null;

                      const levelName =
                        form.levelNames[level] || `Nivel ${level + 1}`;

                      return (
                        <FormControl
                          fullWidth
                          sx={{ mt: 2 }}
                          key={level}
                          size="small"
                        >
                          <InputLabel>{levelName}</InputLabel>
                          <Select
                            label={levelName}
                            value={form.pathValues[level] || ''}
                            onChange={(e) =>
                              handlePathSelection(level, e.target.value)
                            }
                          >
                            {options.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })}

                    {form.pathValues.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        mt={2}
                        mb={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography variant="caption" fontWeight="bold">
                          Ruta seleccionada:
                        </Typography>
                        {form.pathValues.map((p, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <ChevronRightIcon fontSize="small" />}
                            <Chip label={p} size="small" color="primary" />
                          </React.Fragment>
                        ))}
                      </Stack>
                    )}

                    <Divider sx={{ my: 3 }} />
                  </>
                )}

              {/* Agregar opciones */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Valores de "{getCurrentLevelName()}"
              </Typography>

              {form.options.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No hay valores para este nivel. Agrega al menos uno.
                </Alert>
              ) : (
                form.options.map((opt, index) => (
                  <Stack direction="row" spacing={2} mt={2} key={index}>
                    <TextField
                      fullWidth
                      label={`Valor ${index + 1}`}
                      value={opt}
                      onChange={(e) => updateOption(index, e.target.value)}
                    />
                    <IconButton
                      color="error"
                      onClick={() => removeOption(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))
              )}

              {form.options.length < MAX_OPTIONS && (
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  startIcon={<AddIcon />}
                  onClick={addOption}
                >
                  Agregar Valor
                </Button>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {step > 1 && (
            <Button onClick={() => setStep(step - 1)} sx={{ mr: 'auto' }}>
              Atrás
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          {step === 2 && (
            <Button
              variant="contained"
              disabled={!step2Valid}
              onClick={() => setStep(3)}
            >
              Siguiente
            </Button>
          )}
          {step === 3 && (
            <Button
              variant="contained"
              disabled={!step3Valid}
              onClick={saveDemographic}
              startIcon={<SaveIcon />}
            >
              Guardar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* DIALOG DE CONFIRMACIÓN PARA ELIMINAR */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar el demográfico{' '}
            <strong>
              {demographicToDelete !== null
                ? demographics[demographicToDelete]?.levelNames[0]
                : ''}
            </strong>
            ?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
